import React, { useEffect, useMemo, useState } from "react";
import { ColumnsType } from "antd/es/table";
import Footer from "../../../../core/common/Footer";
import { PageBreadcrumb } from "../../../../core/common/BreadCrumbForm";
import { toast } from "react-toastify";
import Datatable from "../../../../core/common/dataTable";
import handleClearError from "../../../../core/methods/handleClearError";
import {
  parseDate,
  parseNumber,
  safeLocaleCompare,
} from "../../../../core/methods/sorterMethods";
import ErrorMessage from "../../../../core/common/ErrorMessage";
import { cellWidth } from "../../../../core/methods/tableCellWidth";
import SearchableSelect from "../../../../core/common/SearchableSelect";
import { useManagerhook } from "../../../../core/customhook/hooks/managerHook";
import { PaginationType } from "../../../../types/common-type";
import useApi from "../../../../core/customhook/useApi";
import webservice_url from "../../../../core/webservice/Webservice_url";
import Loading from "../../../../core/common/custom-loading/Loading";
import { formattedDate } from "../../../../core/methods/formattedDate";
import { useAuth } from "../../../../core/context/useAuthContext";
import { getSearchableHeader } from "../../../../core/utils/getSearchableHeader";
import useLocalStorage from "../../../../core/customhook/hooks/useLocalStorage";
import { useNavigate } from "react-router";
import { all_routes } from "../../../router/all_routes";

interface FormDataProp {
  name: string;
  phone: string;
  email: string;
  manager: string;
}

const initialData: FormDataProp = {
  name: "",
  phone: "",
  email: "",
  manager: "",
};

interface FilterType {
  organization: string;
  project: string;
  location: string;
  program: string;
  department: string;
}

const initialFilter = {
  organization: "",
  project: "",
  location: "",
  program: "",
  department: "",
};

// breadcrumb data
const breadcrumbItem = [{ label: "Add Verifier" }];

const AdminAddVerifier = () => {
  const { auth } = useAuth();
  const { options: userOptions, loading: loadingManager } = useManagerhook();

  const [allItemList, setAllItemList] = useState<any[] | null>(null);
  const [formData, setFormData] = useState<FormDataProp>(initialData);
  const { setLocalStorage } = useLocalStorage();
  const [filterData, setFilterData] = useState<FilterType>(initialFilter);
  const [errors, setErrors] = useState<any>({});
  const [verifierList, setVerifierList] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const navigate = useNavigate();
  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000, // limit of records
    total: 0,
  });

  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});
  type InviteItem = any;
  const filteredData = (Array.isArray(allItemList) ? allItemList : []).filter(
    (item: InviteItem) => {
      return Object.entries(inventoryColumnSearch).every(([key, value]) => {
        if (!value) return true;

        return String(item[key as keyof InviteItem] ?? "")
          .toLowerCase()
          .includes(value.toLowerCase());
      });
    },
  );
  // ---- API ----
  const { callApi, loading: loading_getCases } = useApi<any>(
    webservice_url.get_all_cases_by_filters,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const { callApi: addVerifierAPI, loading } = useApi<any>(
    webservice_url.admin_add_verifier,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const { callApi: getAllVerifiersAPI, loading: loading_getVerifiers } =
    useApi<any>(webservice_url.get_all_verifiers, {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    });

  const fetchVerifierList = async () => {
    try {
      const res = await getAllVerifiersAPI({
        page: 1,
        limit: 100,
      });

      if (res?.success) {
        setVerifierList(res?.data?.rows || []);
      }
    } catch (error: any) {
      console.error("Failed to fetch verifier list:", error?.response);
    }
  };

  useEffect(() => {
    fetchVerifierList();
  }, []);

  // Validation check
  const validationCheck = () => {
    const newError: any = {};
    const { name, phone, email, manager } = formData;

    if (!manager?.trim()) {
      newError.manager = "Please select manager.";
    }
    if (!name?.trim()) {
      newError.name = "Please enter name.";
    }
    if (!email?.trim()) {
      newError.email = "Email is required.";
    }
    if (!phone?.trim()) {
      newError.phone = "Contact number is required.";
    }
    // if (!selectedRows?.length) {
    //   const msg = "Please select atleast 1 case.";
    //   newError.cases = msg;
    //   toast.error(msg);
    // }

    setErrors(newError);
    return Object.keys(newError)?.length == 0;
  };

  // ---- Columns ----
  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      onCell: () => cellWidth(100),
      align: "center",
    },
    {
      title: getSearchableHeader(
        "Case Id",
        "case_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_id",
      key: "case_id",
      onCell: () => cellWidth(190),
      sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
    },
    {
      title: getSearchableHeader(
        "Case Spoc",
        "case_spoc",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_spoc",
      key: "case_spoc",
      onCell: () => cellWidth(140),
      sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
    },
    {
      title: getSearchableHeader(
        "Candidate Id",
        "hrms_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "hrms_id",
      key: "hrms_id",
      onCell: () => cellWidth(160),
      sorter: (a, b) => safeLocaleCompare(a, b, "hrms_id"),
    },
    {
      title: getSearchableHeader(
        "Case Received Date",
        "case_received_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_received_date",
      key: "case_received_date",
      onCell: () => cellWidth(200),
      render: (text: string) => {
        const date = formattedDate(text) || "-";
        return <div>{date}</div>;
      },
      sorter: (a: any, b: any) =>
        parseDate(a.case_received_date).getTime() -
        parseDate(b.case_received_date).getTime(),
    },
    {
      title: getSearchableHeader(
        "Due Date",
        "due_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "due_date",
      key: "due_date",
      onCell: () => cellWidth(140),
      render: (text: string) => {
        const date = formattedDate(text) || "-";
        return <div>{date}</div>;
      },
      sorter: (a: any, b: any) =>
        parseDate(a.due_date).getTime() - parseDate(b.due_date).getTime(),
    },
    // {
    //   title: "Action",
    //   dataIndex: "",
    //   render: (text: string, record: any) => {
    //     return (
    //       <button type="button" className="btn btn-sm btn-tertiary">
    //         <i className="ti ti-check"></i> Assign
    //       </button>
    //     )
    //   }
    // }
  ];

  // handleChange
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    handleClearError(setErrors, name);
  };

  // ======== fetch all managers =========
  // ---- pagination handled - fetching data on button click
  const fetchData = async (current: number, limit: number) => {
    try {
      const res = await callApi({
        org_id: filterData?.organization ?? "",
        project_id: filterData?.project ?? "",
        program_id: filterData?.program ?? "",
        department_id: filterData?.department ?? "",
        manager_id: formData?.manager ?? "",

        page: current,
        limit: limit,
      });
      if (res?.success) {
        const data = res?.data?.rows || [];
        setAllItemList(data);

        // setPagination({
        //   current: current,
        //   pageSize: limit,
        //   total: res?.data?.pagination?.totalRecords || 0
        // })
      }
    } catch (error: any) {
      console.error(error?.response);
    }
  };

  const handleCleanUp = () => {
    setFilterData(initialFilter);
    setFormData(initialData);
    setSelectedRows([]);
    setAllItemList(null);
  };

  const handleAddVerifier = async () => {
    const isValid = validationCheck();
    if (!isValid) return;

    try {
      const payload = {
        agency_id: auth?.agency_id ?? "",
        agency_user_profile: "VERIFIER",
        manager_user_id: formData?.manager ?? "",
        full_name: formData?.name ?? "",
        email: formData?.email ?? "",
        phone: formData?.phone ?? "",
        programs:
          selectedRows?.map((p) => ({
            case_id: p?.assignment_id ?? "",
            package_id: p?.package_id ?? "",

            org_id: filterData?.organization ?? "",
            project_id: filterData?.project ?? "",
            program_id: filterData?.program ?? "",
            department_id: filterData?.department ?? "",
          })) ?? [],
      };
      // console.log("payload:", payload);
      // return;

      const res = await addVerifierAPI(payload);
      if (res?.success) {
        toast.success(res?.message || "Verifier added successfully!");
        handleCleanUp();
        // Refresh verifier list after adding
        await fetchVerifierList();
      }
    } catch (error: any) {
      console.error(error);
      const errMsg = error?.response?.data?.error || "Something went wrong";
      toast.error(errMsg);
    }
    // finally {
    //   setFormData(initialData);
    // }
  };
  const verifierColumns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      onCell: () => cellWidth(100),
      align: "center",
    },
    {
      title: getSearchableHeader(
        "Verifier Name",
        "verifier_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "verifier_name",
      render: (text: string) => (
        <div className="text-capitalize">{text ?? "-"}</div>
      ),
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "verifier_name"),
    },
    {
      title: getSearchableHeader("Verifier Contact", "phone", setInventoryColumnSearch),
      dataIndex: "phone",
      sorter: (a: any, b: any) => parseNumber(a.phone) - parseNumber(b.phone),
    },
    {
      title: getSearchableHeader("Verifier Email", "email", setInventoryColumnSearch),
      dataIndex: "email",
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "email"),
    },
    {
      title: getSearchableHeader(
        "Manager Name",
        "manager_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "manager_name",
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "manager_name"),
    },
    {
      title: getSearchableHeader(
        "Manager Email",
        "manager_email",
        setInventoryColumnSearch,
      ),
      dataIndex: "manager_email",
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "manager_email"),
    },
    {
      title: getSearchableHeader("Manager Contact", "manager_phone", setInventoryColumnSearch),
      dataIndex: "manager_phone",
      sorter: (a: any, b: any) => parseNumber(a.manager_phone) - parseNumber(b.manager_phone),
    },
    {
      title: "Cases",
      dataIndex: "",
      align: "center",
      render: (text: string, record: any) => {
        return (
          <button
            type="button"
            className="btn btn-sm btn-tertiary py-0"
            onClick={() => {
              // storing in localstorage
              const verifierData = {
                name: record?.verifier_name ?? "",
                email: record?.email ?? "",
                mobile: record?.phone ?? "",
                managerId: record?.manager_user_id ?? "",
                managerName: record?.manager_name ?? "",
                managerEmail: record?.manager_email ?? "",
                managerNumber: record?.manager_phone ?? "",
              };
              setLocalStorage("admin_selected_verifier", verifierData);

              navigate(
                `${all_routes.admin_verifier_cases}/${record.verifier_user_id}`,
              );
            }}
          >
            <i className="ti ti-eye"></i>
          </button>
        );
      },
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* PageBreadcrumb */}
        <PageBreadcrumb title="Add Verifier" breadcrumbs={breadcrumbItem} />
        {/* /PageBreadcrumb */}

        {/* add Verifier */}
        <div className="card mb-4">
          <div className="card-body pt-2 pb-3 border-bottom">
            <div className="row gx-3 pt-2">
              <div className="col-6 col-md-4 col-lg-3 ">
                <SearchableSelect
                  label="Manager"
                  name="manager"
                  value={formData?.manager ?? ""}
                  options={userOptions}
                  onChange={(name, value) => {
                    setFormData((prev) => ({ ...prev, manager: value }));
                    handleClearError(setErrors, "manager");
                  }}
                  hasRowGrid={false}
                  error={errors?.manager}
                  loading={loadingManager}
                  disabled={loading}
                  required={true}
                />
              </div>
              <div className="col-6 col-md-4 col-lg-3">
                <label htmlFor="name" className="form-label">
                  Verifier name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control mb-1 text-capitalize `}
                  name="name"
                  id="name"
                  value={formData?.name ?? ""}
                  placeholder="Full name"
                  onChange={handleChange}
                  disabled={!formData?.manager || loading || loading_getCases}
                />
                <ErrorMessage message={errors?.name} />
              </div>
              <div className="col-6 col-md-4 col-lg-3">
                <label htmlFor="email" className="form-label">
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control mb-1"
                  name="email"
                  id="email"
                  value={formData?.email ?? ""}
                  placeholder="you@example.com"
                  onChange={handleChange}
                  disabled={!formData?.manager || loading || loading_getCases}
                />
                <ErrorMessage message={errors?.email} />
              </div>
              <div className="col-6 col-md-4 col-lg-3">
                <label htmlFor="phone" className="form-label">
                  Contact <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-control mb-1"
                  name="phone"
                  value={formData?.phone ?? ""}
                  placeholder="Phone number"
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);

                    setFormData((prev: any) => ({
                      ...prev,
                      phone: value,
                    }));

                    handleClearError(setErrors, "phone");
                  }}
                  disabled={!formData?.manager || loading || loading_getCases}
                />
                <ErrorMessage message={errors?.phone} />
              </div>
            </div>

            <div className="pt pe-2 text-end">
              <button
                type="button"
                onClick={handleAddVerifier}
                className="btn btn-primary"
                disabled={loading}
                style={{ width: "140px" }}
              >
                {loading ? (
                  "Adding..."
                ) : (
                  <>
                    <i className="ti ti-plus"></i> Add Verifier
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="card mt-3">
          <div className="card-header">
            <h5 className="mb-0">Verifier List</h5>
          </div>

          <div className="card-body p-0">
            {loading_getVerifiers ? (
              <Loading />
            ) : (
              <Datatable
                rowKey="verifier_user_id"
                columns={verifierColumns}
                fixedHeader
                tableHeight={650}
                dataSource={verifierList}
                Selection={false}
              />
            )}
          </div>
        </div>

        {loading_getCases ? (
          <div className="card">
            <Loading />
          </div>
        ) : allItemList ? (
          <div className="card mt-3">
            <Datatable
              rowKey="case_id"
              dataSource={filteredData}
              columns={columns}
              fixedHeader
              tableHeight={650}
              Selection={true}
              onSelectionChange={(rows) => {
                setSelectedRows(rows);
                handleClearError(setErrors, "cases");
              }}
            />
          </div>
        ) : null}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AdminAddVerifier;
